import React from 'react'
import classNames from 'classnames'
import { Badge, Button } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBan,
  faCalendarAlt,
  faCalendarCheck,
  faCalendarTimes,
  faCheckCircle,
  faClipboardCheck,
  faFileInvoiceDollar,
  faHandshake,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons'
import { formatMoney } from '../marketplaceConstants'
import { QUOTE_STATUS, formatDate, formatMessageTime, formatVisit } from './requestConstants'

const PROPOSAL_STATUS = {
  pending: { label: 'Esperando respuesta', color: 'warning' },
  accepted: { label: 'Aceptada', color: 'success' },
  superseded: { label: 'Ya no vigente', color: 'secondary' }
}

const EVENTS = {
  visit_confirmed: { icon: faCalendarCheck, color: 'success', text: (message) => `Visita confirmada para el ${formatVisit(message.data.date, message.data.time)}.` },
  visit_done: { icon: faClipboardCheck, color: 'info', text: () => 'El proveedor marcó la visita como realizada.' },
  visit_canceled: { icon: faCalendarTimes, color: 'warning', text: (message) => `El proveedor canceló la visita del ${formatVisit(message.data.date, message.data.time)}.` },
  quote_accepted: { icon: faHandshake, color: 'success', text: (message) => `Presupuesto aceptado por ${formatMoney(message.data.amount)}. ¡Servicio contratado!` },
  quote_rejected: { icon: faTimesCircle, color: 'danger', text: () => 'El propietario rechazó la cotización.' },
  completed: { icon: faCheckCircle, color: 'success', text: () => 'El proveedor marcó el servicio como completado.' },
  canceled: {
    icon: faBan,
    color: 'danger',
    text: (message) => (message.sender_role === 'provider' ? 'El proveedor rechazó la solicitud.' : 'El propietario canceló la solicitud.')
  }
}

const Time = ({ date, mine }) => <div className={classNames('fs--2 text-500 mt-1', { 'text-right': mine })}>{formatMessageTime(date)}</div>

const Bubble = ({ mine, children, date }) => (
  <div className={classNames('d-flex mb-3', mine ? 'justify-content-end' : 'justify-content-start')}>
    <div style={{ maxWidth: '80%' }}>
      <div className={classNames('rounded-lg px-3 py-2', mine ? 'bg-primary text-white' : 'bg-200 text-900')} style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
        {children}
      </div>
      <Time date={date} mine={mine} />
    </div>
  </div>
)

const CardBubble = ({ mine, children, date }) => (
  <div className={classNames('d-flex mb-3', mine ? 'justify-content-end' : 'justify-content-start')}>
    <div style={{ width: 340, maxWidth: '90%' }}>
      <div className="border rounded-lg bg-white p-3">{children}</div>
      <Time date={date} mine={mine} />
    </div>
  </div>
)

const QuoteCard = ({ message, mine, quote, canRespond, busy, onAccept, onReject }) => {
  if (!quote) return null
  const status = QUOTE_STATUS[quote.status] || QUOTE_STATUS.pending
  const expired = quote.status === 'pending' && quote.is_expired

  return (
    <CardBubble mine={mine} date={message.created_at}>
      <div className="d-flex align-items-center mb-2">
        <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-primary mr-2" />
        <span className="font-weight-semi-bold text-900 mr-auto">Cotización</span>
        <Badge color={`soft-${expired ? 'secondary' : status.color}`} pill>
          {expired ? 'Vencida' : status.label}
        </Badge>
      </div>
      <p className="fs-2 font-weight-bold text-900 mb-2">{formatMoney(quote.amount)}</p>
      <p className="fs--1 text-700 mb-2" style={{ whiteSpace: 'pre-line' }}>
        {quote.description}
      </p>
      {quote.valid_until && <p className="fs--2 text-600 mb-0">Válida hasta el {formatDate(quote.valid_until)}</p>}
      {canRespond && quote.status === 'pending' && !expired && (
        <div className="d-flex mt-3" style={{ gap: 8 }}>
          <Button color="primary" size="sm" className="flex-grow-1" disabled={busy} onClick={() => onAccept(quote)}>
            Aceptar presupuesto
          </Button>
          <Button color="falcon-default" size="sm" disabled={busy} onClick={() => onReject(quote)}>
            Rechazar
          </Button>
        </div>
      )}
      {canRespond && expired && <p className="fs--2 text-danger mt-2 mb-0">Esta cotización venció. Pídele al proveedor una nueva por el chat.</p>}
    </CardBubble>
  )
}

const ProposalCard = ({ message, mine, canAccept, busy, onAccept }) => {
  const status = PROPOSAL_STATUS[message.data.status] || PROPOSAL_STATUS.pending

  return (
    <CardBubble mine={mine} date={message.created_at}>
      <div className="d-flex align-items-center mb-2">
        <FontAwesomeIcon icon={faCalendarAlt} className="text-primary mr-2" />
        <span className="font-weight-semi-bold text-900 mr-auto">Propuesta de otra fecha</span>
        <Badge color={`soft-${status.color}`} pill>
          {status.label}
        </Badge>
      </div>
      <p className="mb-0 text-900">{formatVisit(message.data.date, message.data.time)}</p>
      {canAccept && message.data.status === 'pending' && (
        <Button color="primary" size="sm" block className="mt-3" disabled={busy} onClick={() => onAccept(message)}>
          Aceptar fecha
        </Button>
      )}
    </CardBubble>
  )
}

const ConversationMessage = ({ message, side, quote, can, busy, onAcceptQuote, onRejectQuote, onAcceptProposal }) => {
  const mine = message.sender_role === side

  if (message.type === 'text') {
    return (
      <Bubble mine={mine} date={message.created_at}>
        {message.body}
      </Bubble>
    )
  }

  if (message.type === 'request_created') {
    return (
      <CardBubble mine={mine} date={message.created_at}>
        <p className="font-weight-semi-bold text-900 mb-1">Solicitud de visita / cotización</p>
        <p className="fs--1 text-700 mb-2" style={{ whiteSpace: 'pre-line' }}>
          {message.body}
        </p>
        <p className="fs--2 text-600 mb-0">Fecha sugerida: {formatVisit(message.data.suggested_date, message.data.suggested_time)}</p>
      </CardBubble>
    )
  }

  if (message.type === 'quote') {
    return (
      <QuoteCard
        message={message}
        mine={mine}
        quote={quote}
        canRespond={side === 'owner' && can.respond_quote}
        busy={busy}
        onAccept={onAcceptQuote}
        onReject={onRejectQuote}
      />
    )
  }

  if (message.type === 'visit_proposal') {
    return <ProposalCard message={message} mine={mine} canAccept={side === 'owner' && can.accept_proposal} busy={busy} onAccept={onAcceptProposal} />
  }

  const event = EVENTS[message.type]
  if (!event) return null

  return (
    <div className="text-center mb-3">
      <span className={`d-inline-block rounded-pill fs--1 px-3 py-1 bg-soft-${event.color} text-${event.color}`}>
        <FontAwesomeIcon icon={event.icon} className="mr-1" />
        {event.text(message)}
      </span>
      {message.body && <p className="fs--1 text-700 font-italic mt-1 mb-0">“{message.body}”</p>}
      <div className="fs--2 text-500 mt-1">{formatMessageTime(message.created_at)}</div>
    </div>
  )
}

export default ConversationMessage
